import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { schedulingServiceEnabled } from '../../lib/features'
import { useIsDesktop } from '../shell/useIsDesktop'

const ICON = { width: 16, height: 16, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.8, strokeLinecap: 'round', strokeLinejoin: 'round', 'aria-hidden': true } as const

function HomeIcon() {
  return (
    <svg {...ICON}>
      <path d="M3 11l9-8 9 8" />
      <path d="M5 10v10h14V10" />
    </svg>
  )
}

function InboxIcon() {
  return (
    <svg {...ICON}>
      <path d="M22 12h-6l-2 3h-4l-2-3H2" />
      <path d="M5.5 5h13L22 12v6a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2v-6z" />
    </svg>
  )
}

function UsersIcon() {
  return (
    <svg {...ICON}>
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  )
}

function CalendarPlusIcon() {
  return (
    <svg {...ICON}>
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <path d="M16 2v4M8 2v4M3 10h18M12 14v4M10 16h4" />
    </svg>
  )
}

function TargetIcon() {
  return (
    <svg {...ICON}>
      <circle cx="12" cy="12" r="9" />
      <circle cx="12" cy="12" r="5" />
      <circle cx="12" cy="12" r="1" fill="currentColor" stroke="none" />
    </svg>
  )
}

function ReportIcon() {
  return (
    <svg {...ICON}>
      <path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" />
      <path d="M14 3v6h6M8 17v-3M12 17v-6M16 17v-4" />
    </svg>
  )
}

function ClockIcon() {
  return (
    <svg {...ICON}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 3" />
    </svg>
  )
}

function GridIcon() {
  return (
    <svg {...ICON}>
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
      <rect x="14" y="14" width="7" height="7" rx="1" />
    </svg>
  )
}

const ITEMS: { to: string; label: string; icon: ReactNode; end?: boolean; primary?: boolean }[] = [
  { to: '/supervisor', label: 'Dashboard', icon: <HomeIcon />, end: true },
  { to: '/supervisor/approval-inbox', label: 'Approval Inbox', icon: <InboxIcon /> },
  { to: '/supervisor/personnel', label: 'Personnel', icon: <UsersIcon /> },
  { to: '/supervisor/daily-assignments', label: 'Daily Assignments', icon: <CalendarPlusIcon /> },
  { to: '/supervisor/staffing-targets', label: 'Staffing Targets', icon: <TargetIcon /> },
  { to: '/supervisor/open-shifts', label: 'Open Shifts', icon: <ClockIcon /> },
  { to: '/supervisor/reports', label: 'Reports', icon: <ReportIcon /> },
  { to: '/supervisor/planning-board', label: 'Planning Board', icon: <GridIcon />, primary: true },
]

/**
 * The supervisor workspace's navigation: a row of buttons at the top right of
 * the content on every supervisor page, at the same place on each: it is as wide as the widest page, so its right edge does not move with a page's own content width. The page being viewed reads as pressed,
 * and says so to assistive technology with aria-current; a person's profile is
 * part of Personnel. A page with work in progress passes `beforeLeave`, which is
 * asked before the supervisor goes to another page and can refuse to let them.
 */
export default function SupervisorNav({ beforeLeave }: { beforeLeave?: () => boolean }) {
  const { pathname } = useLocation()
  const listRef = useRef<HTMLUListElement>(null)
  const isDesktop = useIsDesktop()
  // The rail starts under the top bar, which runs the full width of the screen.
  const [top, setTop] = useState(0)
  useLayoutEffect(() => {
    if (!isDesktop) return
    const place = () => setTop(document.querySelector('header')?.getBoundingClientRect().bottom ?? 0)
    place()
    window.addEventListener('resize', place)
    return () => window.removeEventListener('resize', place)
  }, [isDesktop])

  // Below `sm` the buttons sit in one row that scrolls sideways, so the page
  // being viewed is brought into view when it is off the edge.
  useEffect(() => {
    listRef.current?.querySelector('[aria-current="page"]')?.scrollIntoView?.({ block: 'nearest', inline: 'center' })
  }, [pathname])

  const items = ITEMS.filter((item) => item.to !== '/supervisor/open-shifts' || schedulingServiceEnabled())

  // From `lg` up the navigation is a rail down the left edge, icons only until
  // it is hovered or holds focus, when it opens over the page to show labels.
  if (isDesktop) {
    return (
      <nav
        aria-label="Supervisor"
        data-supervisor-rail
        style={{ top }}
        className="group/rail fixed bottom-0 left-0 z-40 flex w-16 flex-col overflow-hidden border-r border-line bg-canvas pt-4 transition-[width,box-shadow] duration-150 hover:w-60 hover:shadow-lg focus-within:w-60 focus-within:shadow-lg print:hidden"
      >
        <ul className="flex flex-col gap-1 px-2">
          {items.map((item) => (
            <li key={item.to}>
              <NavLink
                to={item.to}
                end={item.end}
                onClick={(event) => {
                  if (pathname !== item.to && beforeLeave && !beforeLeave()) event.preventDefault()
                }}
                className={({ isActive }) =>
                  `flex min-h-11 items-center gap-3 rounded-control border px-[15px] text-small font-bold whitespace-nowrap ${
                    isActive
                      ? 'border-primary bg-selected shadow-inner'
                      : item.primary
                        ? 'border-transparent bg-primary text-primary-ink hover:bg-primary-hover'
                        : 'border-transparent hover:bg-surface-2'
                  }`
                }
              >
                <span className="flex-none">{item.icon}</span>
                <span className="opacity-0 transition-opacity duration-150 group-hover/rail:opacity-100 group-focus-within/rail:opacity-100">{item.label}</span>
              </NavLink>
            </li>
          ))}
        </ul>
        <span aria-hidden="true" className="mt-auto flex h-11 flex-none items-center justify-center text-ink-muted">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="transition-transform duration-150 group-hover/rail:rotate-180 group-focus-within/rail:rotate-180">
            <path d="m9 6 6 6-6 6" />
          </svg>
        </span>
      </nav>
    )
  }

  return (
    <nav aria-label="Supervisor" className="mx-auto mb-5 w-full max-w-[1400px] flex-none lg:mb-6 print:hidden">
      <ul ref={listRef} className="flex items-center gap-2 overflow-x-auto py-0.5 sm:flex-wrap sm:justify-end sm:overflow-visible">
        {items.map((item) => (
          <li key={item.to} className="flex-none">
            <NavLink
              to={item.to}
              end={item.end}
              onClick={(event) => {
                if (pathname !== item.to && beforeLeave && !beforeLeave()) event.preventDefault()
              }}
              className={({ isActive }) =>
                `flex min-h-11 items-center gap-2 rounded-control border px-4 text-small font-bold whitespace-nowrap ${
                  isActive
                    ? 'border-primary bg-selected shadow-inner'
                    : item.primary
                      ? 'border-transparent bg-primary text-primary-ink hover:bg-primary-hover'
                      : 'border-line-strong hover:bg-surface-2'
                }`
              }
            >
              {item.icon}
              {item.label}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  )
}
